import _ from 'lodash';
import moment from 'moment';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import CustomersApi from '@/api/CustomersApi';
import SubscriptionStateApi from '@/api/SubscriptionStateApi';
import featureFlags from '@/api/featureFlag';
import destination from '@/utils/dormant-winback-destination';
import redesignDestination from '@/utils/redesign-destination';
import { isIosNativeHost } from '@/utils/deviceUtils';

async function isDormantWinback() {
  const MONTHS_UNTIL_WINBACK_EXPERIENCE = 3;
  const { isDormant, since } = await SubscriptionStateApi.simplifiedSubscriptionState();
  return isDormant && since < moment().subtract(MONTHS_UNTIL_WINBACK_EXPERIENCE, 'month').format();
}

async function performReset(query) {
  if (await hasParkedPaidForBenefit()) {
    return resetAndReturnToApp(query);
  }
  const variant = await featureFlags.getTreatment('dormant-winback');
  DDLogs.log('Resetting dormant customer without the winback welcome screen', { variant });
  // Required lazily: store modules import the router, which imports this file.
  // eslint-disable-next-line global-require
  const store = require('@/store').default;
  // Campaign links land here with a promo code; grant it so the plan page can present it.
  if (query.promoCode) {
    try {
      await store.dispatch('benefits/createBenefit', query.promoCode);
      Segment.trackUserGot('PromotionForDormant', { promoCode: query.promoCode });
    } catch (error) {
      DDLogs.error('Failed granting the promo code benefit for a returning dormant', { error });
    }
  }
  // Deliberately not awaited — the reset can take a minute or two, and the customer continues
  // into the app while it runs; the subscription flow's Done screen is what waits for the scrape.
  resetInBackground(store, destination.promoCodeForVariant(variant));
  return destination.destinationForVariant(variant, query, { redesign: await continuesInRedesign(store, query) });
}

// Only when the redirect will admit the visit into v3, outside the iOS app (no dormant payment CTA
// there); otherwise the customer keeps going straight to Vue 2's payment flow.
async function continuesInRedesign(store, query) {
  if (isIosNativeHost() || redesignDestination.legacyReasonFor({ query }, { incognito: store.state.session.incognito })) {
    return false;
  }
  try {
    await store.dispatch('featureFlags/init');
    return Boolean(store.getters['featureFlags/redesignEnabled']);
  } catch (error) {
    DDLogs.error('Failed reading the redesign flag for a returning dormant', { error });
    return false;
  }
}

async function hasParkedPaidForBenefit() {
  try {
    const { state } = await SubscriptionStateApi.fetchSubscriptionState();
    return state === 'free';
  } catch (error) {
    DDLogs.error('Failed checking for a parked paid-for benefit', { error });
    return false;
  }
}

async function resetAndReturnToApp(query) {
  // eslint-disable-next-line global-require
  const store = require('@/store').default;
  await CustomersApi.resetForDormant();
  await store.dispatch('subscriptionState/fetchSubscriptionState');
  try {
    await store.dispatch('benefits/initBenefits');
  } catch (error) {
    DDLogs.error('Failed reloading benefits after resetting a dormant with a parked paid-for benefit', { error });
  }
  return { path: '/sr', query: { ..._.omit(query, 'promoCode'), fromDormantReset: 'true' } };
}

// Once the reset lands, reload benefits so a benefit it granted server-side shows up in-session.
async function resetInBackground(store, promoCode) {
  try {
    await CustomersApi.resetForDormant(promoCode);
    await store.dispatch('benefits/initBenefits');
  } catch (error) {
    DDLogs.error('Failed resetting cashflow for a returning dormant', { error });
  }
}

// The reset the winback screen used to perform — it rebuilds the cashflow and starts the scrape
// the subscription flow waits on. Several guards can ask for it on one navigation, so the
// in-flight promise is what stops a second reset firing; it is deliberately not cleared on
// success — one reset per page load is the point.
let resetPromise = null;

function resetDormantAndGetDestination(query = {}) {
  if (!resetPromise) {
    resetPromise = performReset(query).catch(error => {
      // Let a failed reset be retried rather than wedging the customer on a dead promise.
      resetPromise = null;
      throw error;
    });
  }
  return resetPromise;
}

// A failed reset must not abort the navigation and strand the customer on a blank page — the
// winback screen this replaced showed a ServerError, so send them to the same error screen.
async function nextForDormantWinback(to, next) {
  try {
    next(await resetDormantAndGetDestination(to.query));
  } catch (error) {
    DDLogs.error('Failed resetting cashflow for a returning dormant', { error });
    next({ path: '/reset-cashflow/scrape-error' });
  }
}

export default isDormantWinback;
export { resetDormantAndGetDestination, nextForDormantWinback };
